import { textColorFields } from '../_shared/textColor.js';
import { playButtonField } from '../_shared/videoPlayButton.js';
import { universalFields } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
import { patternFields } from '../_shared/pattern.js';
const SLIDER_SCHEME_OPTIONS = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background 1' },
    { value: 'background-2', label: 'Background 2' },
    { value: 'inverse', label: 'Inverse' },
];
/**
 * facebook-testimonials — Shrine "Facebook testimonials" at CONFIG-BY-CONFIG parity. A heading (+ the shared
 * word-HIGHLIGHT engine and an optional pattern background) over a responsive grid/slider of Facebook-style
 * `.fb-post` cards. Each POST is an inline `column` child block (Shrine block type `column`): author + avatar
 * + verified badge + time, post text/image/video, reaction icons + counts, Like/Comment/Share CTAs, and up
 * to two comments.
 *
 * The Shopify-platform-only knobs are DEFERRED with justification (see Component.tsx): `display_id`
 * (section-merge helper), the five `color_scheme` selects map onto our 3-way `colorScheme`/slider-scheme
 * tokens, the `custom_image_background` pair (Shopify img_url pipeline), reveal/scroll animations, and the
 * live Splide carousel JS (autoplay/loop need a client island — the controls + native scroll-snap render).
 */
export const facebookTestimonialsSchema = {
    type: 'facebook-testimonials',
    name: 'Facebook testimonials',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── General (Shrine HEADER: General) ─────────────────────────────────────
        {
            id: 'visibility',
            type: 'select',
            label: 'Visibility',
            group: 'General',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'Always display' },
                { value: 'desktop-hidden', label: 'Hide on desktop' },
                { value: 'mobile-hidden', label: 'Hide on mobile' },
            ],
        },
        {
            id: 'heading',
            type: 'richtext',
            label: 'Heading',
            group: 'General',
            default: { format: 'html', html: '<p>Facebook testimonials</p>' },
            info: 'Bold a word to apply highlight 1, italic to apply highlight 2.',
        },
        ...headingHighlightFields(1, 'General').map((f) => f.id === 'headingHighlight1'
            ? { ...f, info: 'Bold a word in the heading to style it with this.' }
            : f),
        ...headingHighlightFields(2, 'General').map((f) => f.id === 'headingHighlight2'
            ? { ...f, info: 'Italicize a word in the heading to style it with this.' }
            : f),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'General',
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customDesktopSize',
            type: 'range',
            label: 'Custom desktop size',
            group: 'General',
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'customMobileSize',
            type: 'range',
            label: 'Custom mobile size',
            group: 'General',
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'text',
            type: 'richtext',
            label: 'Text',
            group: 'General',
        },
        // ── Background pattern (shared engine — Shrine pattern_bg cluster). ────────
        ...patternFields('General'),
        // ── Slider (Shrine HEADER: Slider) ───────────────────────────────────────
        {
            id: 'sliderType',
            type: 'select',
            label: 'Type',
            group: 'Slider',
            default: 'slide',
            info: 'Classic (Slide) or infinite (Loop). The looping/autoplay carousel needs a client island.',
            options: [
                { value: 'slide', label: 'Classic' },
                { value: 'loop', label: 'Infinite' },
            ],
        },
        {
            id: 'autoplay',
            type: 'checkbox',
            label: 'Enable autoplay',
            group: 'Slider',
            default: false,
        },
        {
            id: 'autoplaySpeed',
            type: 'range',
            label: 'Autoplay speed',
            group: 'Slider',
            min: 1,
            max: 15,
            step: 0.5,
            unit: 's',
            default: 5,
            visibleWhen: { field: 'autoplay', equals: true },
        },
        {
            id: 'transparentArrows',
            type: 'checkbox',
            label: 'Transparent arrows',
            group: 'Slider',
            default: true,
        },
        {
            id: 'arrowsScheme',
            type: 'select',
            label: 'Arrows color scheme',
            group: 'Slider',
            default: 'inverse',
            options: SLIDER_SCHEME_OPTIONS,
        },
        {
            id: 'dotsScheme',
            type: 'select',
            label: 'Dots color scheme',
            group: 'Slider',
            default: 'inverse',
            options: SLIDER_SCHEME_OPTIONS,
        },
        // ── Desktop (Shrine HEADER: Desktop) ─────────────────────────────────────
        {
            id: 'desktopFullPage',
            type: 'checkbox',
            label: 'Full page width',
            group: 'Desktop',
            default: false,
        },
        {
            id: 'columnsDesktop',
            type: 'range',
            label: 'Slides per page',
            group: 'Desktop',
            min: 1,
            max: 10,
            step: 1,
            default: 3,
        },
        {
            id: 'sliderDesktop',
            type: 'checkbox',
            label: 'Enable desktop slider',
            group: 'Desktop',
            default: false,
            info: 'When off, cards lay out as a static grid on desktop.',
        },
        {
            id: 'perMoveDesktop',
            type: 'range',
            label: 'Slides to scroll on one move',
            group: 'Desktop',
            min: 1,
            max: 10,
            step: 1,
            default: 1,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopSpacing',
            type: 'range',
            label: 'Spacing',
            group: 'Desktop',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 24,
        },
        {
            id: 'desktopSidePadding',
            type: 'range',
            label: 'Preview of prev & next slide',
            group: 'Desktop',
            min: 0,
            max: 150,
            step: 5,
            unit: 'px',
            default: 0,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopPaddingCalc',
            type: 'checkbox',
            label: 'Disable empty preview on first & last slide',
            group: 'Desktop',
            default: true,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopAdaptiveHeight',
            type: 'checkbox',
            label: 'Adaptive height',
            group: 'Desktop',
            default: false,
            info: 'Only applies when Slides per page is 1.',
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopDots',
            type: 'select',
            label: 'Dots position',
            group: 'Desktop',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
            ],
        },
        {
            id: 'desktopArrows',
            type: 'select',
            label: 'Arrows position',
            group: 'Desktop',
            default: 'sides',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'sides', label: 'On sides of the slider' },
            ],
        },
        // ── Mobile (Shrine HEADER: Mobile) ───────────────────────────────────────
        {
            id: 'sliderMobile',
            type: 'checkbox',
            label: 'Enable mobile slider',
            group: 'Mobile',
            default: true,
        },
        {
            id: 'mobilePreview',
            type: 'checkbox',
            label: 'Preview of prev & next slides',
            group: 'Mobile',
            default: false,
            visibleWhen: { field: 'sliderMobile', equals: true },
        },
        {
            id: 'mobileAdaptiveHeight',
            type: 'checkbox',
            label: 'Adaptive height',
            group: 'Mobile',
            default: false,
            visibleWhen: { field: 'sliderMobile', equals: true },
        },
        {
            id: 'mobileDots',
            type: 'select',
            label: 'Dots position',
            group: 'Mobile',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
            ],
        },
        {
            id: 'mobileArrows',
            type: 'select',
            label: 'Arrows position',
            group: 'Mobile',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'sides', label: 'On sides of the slider' },
            ],
        },
        // ── Posts layout (Shrine HEADER: Posts layout) ───────────────────────────
        {
            id: 'postBgColor',
            type: 'color',
            label: 'Background color',
            group: 'Posts layout',
            default: { ref: 'custom', value: '#ffffff' },
        },
        {
            id: 'postTextColor',
            type: 'color',
            label: 'Text color',
            group: 'Posts layout',
            default: { ref: 'custom', value: '#101010' },
        },
        {
            id: 'commentsBgColor',
            type: 'color',
            label: 'Comments background color',
            group: 'Posts layout',
            default: { ref: 'custom', value: '#F0F2F5' },
        },
        {
            id: 'separatorsColor',
            type: 'color',
            label: 'Separators color',
            group: 'Posts layout',
            default: { ref: 'custom', value: '#a4b0be' },
        },
        {
            id: 'postBorderColor',
            type: 'color',
            label: 'Post container border color',
            group: 'Posts layout',
            default: { ref: 'custom', value: '#DFDFDF' },
        },
        {
            id: 'borderWidth',
            type: 'range',
            label: 'Post container border thickness',
            group: 'Posts layout',
            min: 0,
            max: 10,
            step: 1,
            unit: 'px',
            default: 1,
        },
        // ── Labels (Shrine HEADER: Labels) ───────────────────────────────────────
        {
            id: 'likeLabel',
            type: 'text',
            label: 'Like label',
            group: 'Labels',
            default: 'Like',
            defaultKey: 'social.like',
            maxLength: 40,
        },
        {
            id: 'commentLabel',
            type: 'text',
            label: 'Comment label',
            group: 'Labels',
            default: 'Comment',
            defaultKey: 'social.comment',
            maxLength: 40,
        },
        {
            id: 'replyLabel',
            type: 'text',
            label: 'Reply label',
            group: 'Labels',
            default: 'Reply',
            defaultKey: 'social.reply',
            maxLength: 40,
        },
        {
            id: 'shareLabel',
            type: 'text',
            label: 'Share label',
            group: 'Labels',
            default: 'Share',
            defaultKey: 'social.share',
            maxLength: 40,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // ── Section (universal: color scheme, padding_top/bottom, width) ─────────
        ...universalFields,
    ],
    // The POST inline block (Shrine `column`): author/avatar/verified/time, text/image/video, reactions,
    // Like/Comment/Share CTAs, and up to two comments.
    blocks: [
        {
            type: 'column',
            name: 'Testimonial',
            max: 20,
            fields: [
                // Post author
                {
                    id: 'postAuthor',
                    type: 'text',
                    label: 'Author',
                    group: 'Post author',
                    default: 'Author name',
                    defaultKey: 'section.author_name',
                    maxLength: 80,
                },
                {
                    id: 'postAuthorAvatar',
                    type: 'image',
                    label: 'Author profile picture',
                    group: 'Post author',
                },
                {
                    id: 'postAuthorVerified',
                    type: 'checkbox',
                    label: 'Author verified',
                    group: 'Post author',
                    default: false,
                },
                {
                    id: 'postTime',
                    type: 'text',
                    label: 'Time posted',
                    group: 'Post author',
                    default: '2h ago',
                    maxLength: 40,
                },
                // Post content
                {
                    id: 'postText',
                    type: 'richtext',
                    label: 'Text',
                    group: 'Post content',
                    default: {
                        format: 'html',
                        html: "<p>Shrine theme has made a huge impact on my store's CVR!</p>",
                    },
                },
                { id: 'postImage', type: 'image', label: 'Image', group: 'Post content' },
                { id: 'postVideo', type: 'video', label: 'Video', group: 'Post content' },
                // ÉTEINT PAR DÉFAUT (20/09/2026, fondatrice : « by default it shouldn't show ») — un post vidéo
                // est d'abord une vignette qui bouge dans un mur d'avis ; le disque n'apparaît que si elle le
                // demande, post par post.
                playButtonField({
                    defaultOn: false,
                    group: 'Post content',
                    visibleWhen: { field: 'postVideo', empty: false },
                }),
                {
                    id: 'postVideoThumbnail',
                    type: 'image',
                    label: 'Video thumbnail',
                    group: 'Post content',
                    info: 'If empty, the first frame of the video is displayed.',
                },
                // Post reactions
                {
                    id: 'postReactions',
                    type: 'text',
                    label: 'Displayed reactions',
                    group: 'Post reactions',
                    default: 'like, love, care',
                    info: 'Comma-separated. Options: like, love, care, haha, wow, sad, angry.',
                    maxLength: 80,
                },
                {
                    id: 'postReactionsText',
                    type: 'text',
                    label: 'Reactions text',
                    group: 'Post reactions',
                    default: '1.1k',
                    maxLength: 40,
                },
                {
                    id: 'postCommentsText',
                    type: 'text',
                    label: 'Comments text',
                    group: 'Post reactions',
                    default: '26 Comments',
                    maxLength: 40,
                },
                // Comment #1
                {
                    id: 'comment1Author',
                    type: 'text',
                    label: 'Author name',
                    group: 'Comment #1',
                    default: 'Comment author',
                    defaultKey: 'section.comment_author',
                    maxLength: 80,
                },
                {
                    id: 'comment1AuthorAvatar',
                    type: 'image',
                    label: 'Author profile picture',
                    group: 'Comment #1',
                },
                {
                    id: 'comment1AuthorVerified',
                    type: 'checkbox',
                    label: 'Author verified',
                    group: 'Comment #1',
                    default: false,
                },
                {
                    id: 'comment1Time',
                    type: 'text',
                    label: 'Time posted',
                    group: 'Comment #1',
                    default: '2h ago',
                    maxLength: 40,
                },
                { id: 'comment1Text', type: 'richtext', label: 'Text', group: 'Comment #1' },
                // Comment #2
                {
                    id: 'comment2Author',
                    type: 'text',
                    label: 'Author name',
                    group: 'Comment #2',
                    default: 'Comment author',
                    defaultKey: 'section.comment_author',
                    maxLength: 80,
                },
                {
                    id: 'comment2AuthorAvatar',
                    type: 'image',
                    label: 'Author profile picture',
                    group: 'Comment #2',
                },
                {
                    id: 'comment2AuthorVerified',
                    type: 'checkbox',
                    label: 'Author verified',
                    group: 'Comment #2',
                    default: false,
                },
                {
                    id: 'comment2Time',
                    type: 'text',
                    label: 'Time posted',
                    group: 'Comment #2',
                    default: '3h ago',
                    maxLength: 40,
                },
                { id: 'comment2Text', type: 'richtext', label: 'Text', group: 'Comment #2' },
            ],
        },
    ],
    maxBlocks: 20,
};
export const facebookTestimonialsPresets = [
    {
        name: 'Facebook testimonials',
        settings: {
            visibility: 'always-display',
            heading: { format: 'html', html: '<p>Facebook testimonials</p>' },
            headingSize: 'h1',
            headingHighlight1: 'solid-color',
            headingHighlight1Color: { ref: 'theme', token: 'accent' },
            headingHighlight2: 'none',
            columnsDesktop: 3,
            desktopSpacing: 24,
            sliderType: 'slide',
            autoplay: false,
            transparentArrows: true,
            arrowsScheme: 'inverse',
            dotsScheme: 'inverse',
            desktopFullPage: false,
            sliderDesktop: false,
            desktopArrows: 'sides',
            desktopDots: 'under',
            sliderMobile: true,
            mobileArrows: 'under',
            mobileDots: 'under',
            postBgColor: { ref: 'custom', value: '#ffffff' },
            postTextColor: { ref: 'custom', value: '#101010' },
            commentsBgColor: { ref: 'custom', value: '#F0F2F5' },
            separatorsColor: { ref: 'custom', value: '#a4b0be' },
            postBorderColor: { ref: 'custom', value: '#DFDFDF' },
            borderWidth: 1,
            // The four labels were repeated here, identical to the fields' own defaults.
            colorScheme: 'light',
            paddingTop: 36,
            paddingBottom: 36,
            mobilePaddingTop: 27,
            mobilePaddingBottom: 27,
            width: 'standard',
        },
        // Three default posts — each an empty `column` block, so the block-field defaults fill in exactly the
        // Shrine defaults (Author name / 2h ago / the CVR quote / like,love,care / 1.1k / 26 Comments).
        blocks: [{ type: 'column' }, { type: 'column' }, { type: 'column' }],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const facebookTestimonialsMeta = {
    label: 'Facebook testimonials',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map